// Shared helpers of the Analyst Portfolios app. The asset-class colours and the SAA
// tilt palette/order are copied from PortfolioViewerApp.jsx (not exported there) so
// this app's tables and charts look exactly like the Portfolio Viewer's; the viewer
// itself is not edited.
import { useEffect, useState } from 'react';
import api from '../../services/api';

export const API = '/api/analyst-portfolios';

export const ASSET_CLASS_COLORS = {
  'Global Alpha': '#BBCBD7',
  'Global Alpha - D': '#BBCBD7',
  'Global Alpha - G': '#BBCBD7',
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Global Equities': '#5E819C',
  'Global Equities - H': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Cash': '#EFE6D5',
  'Government Bonds': '#F3BD48',
  'Fixed Income - D': '#F3BD48',
  'Corporate Debt': '#AA8090',
  'Private Debt': '#874564',
  'Private Equity': '#6C757D',
  'Real Assets (Private Market)': '#127080',
  'Alternatives': '#9B59B6',
  'Emerging Markets': '#34495E',
};

export const getAssetClassColor = (assetClass, index) => {
  if (ASSET_CLASS_COLORS[assetClass]) return ASSET_CLASS_COLORS[assetClass];
  const fallback = ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#E74C3C', '#9B59B6', '#1ABC9C',
    '#E67E22', '#34495E', '#7F8C8D', '#2C3E50', '#27AE60', '#8E44AD', '#D35400', '#16A085'];
  return fallback[index % fallback.length];
};

const TILT_AC_COLORS = {
  'Australian Equities': '#011E41', 'International Equities': '#5E819C', 'Global Equities': '#5E819C',
  'Real Estate': '#A6C6C1', 'Infrastructure': '#337B91', 'Cash': '#C9A227',
  'Government Bonds': '#F3BD48', 'Corporate Debt': '#AA8090', 'Private Debt': '#874564',
  'Private Equity': '#6C757D', 'Alternatives': '#9B59B6', 'Emerging Markets': '#34495E',
};
const TILT_AC_ORDER = [
  'Australian Equities', 'International Equities', 'EM Equities', 'Real Estate', 'Infrastructure',
  'Global Alpha - G', 'Global Alpha', 'Private Equity', 'Cash', 'Government Bonds', 'Corporate Debt',
  'Private Debt', 'Private Infrastructure', 'Global Alpha - D',
];
export const tiltAcRank = (ac) => { const i = TILT_AC_ORDER.indexOf(ac); return i === -1 ? 999 : i; };
export const tiltAcColour = (ac, i) => TILT_AC_COLORS[ac]
  || ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#874564', '#127080', '#9B59B6', '#E67E22'][i % 8];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// '2026-05-12' -> '12 May 26' (the viewer's en-AU short date)
export const fmtDate = (iso) => {
  if (!iso) return '–';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return MONTHS_SHORT[+m - 1] ? `${d} ${MONTHS_SHORT[+m - 1]} ${y.slice(2)}` : String(iso);
};
// '2026-09-30' -> '30/09/26' (the deck's table headers)
export const fmtDmy = (iso) => {
  if (!iso) return '–';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y.slice(2)}`;
};
// '2026-07-31' -> 'Jul-26'
export const fmtMonth = (iso) => {
  if (!iso) return '–';
  const [y, m] = String(iso).split('-');
  return MONTHS_SHORT[+m - 1] ? `${MONTHS_SHORT[+m - 1]}-${y.slice(2)}` : String(iso);
};

// Percent with a fixed number of decimals; values that round to zero print as 0 (never -0).
export const fmtPct = (v, dp = 1, signed = false) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '–';
  const eps = 0.5 * (10 ** -dp);
  const n = Math.abs(Number(v)) < eps ? 0 : Number(v);
  return `${signed && n > 0 ? '+' : ''}${n.toFixed(dp)}%`;
};
export const fmtNum = (v, dp = 2) => (v === null || v === undefined ? '–' : Number(v).toFixed(dp));

// Fund performance columns (screener /funds-by-tr-codes), as on the viewer's Target Weights.
export const PERF_COLUMNS = [
  { key: 'mtd', label: 'MTD' }, { key: 'return_1m', label: '1m' }, { key: 'return_3m', label: '3m' },
  { key: 'return_6m', label: '6m' }, { key: 'return_12m', label: '12m' }, { key: 'return_3yr', label: '3yr' },
  { key: 'return_5yr', label: '5yr' },
];

/** Readable message from an axios error, including a structured `detail`. */
export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.message) return [d.message, ...(d.errors || []), ...(d.breaches || []), ...(d.check_errors || [])].join(' · ');
  return JSON.stringify(d);
};

/** GET with stale-response protection; re-fetches when the url, params or reloadToken
    change. Each result is stored with the request key it answers, so a result for an
    older request is never shown and "loading" is simply "no result for this key yet".
    keepStale: keep showing the previous result while a reload is in flight (used for the
    book list and holdings, so a save or fee change does not blank the page). */
export function useApiGet(url, params, reloadToken = 0, keepStale = false) {
  const paramsKey = params ? JSON.stringify(params) : '';
  const key = url ? `${url}|${paramsKey}|${reloadToken}` : null;
  const [state, setState] = useState({ key: null, data: null, error: null });
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    api.get(url, { params: paramsKey ? JSON.parse(paramsKey) : undefined })
      .then((r) => { if (live) setState({ key, data: r.data, error: null }); })
      .catch((e) => { if (live) setState({ key, data: null, error: apiError(e) }); });
    return () => { live = false; };
  }, [key, url, paramsKey]);
  const current = key !== null && state.key === key;
  return { data: current || keepStale ? state.data : null, error: current ? state.error : null,
    loading: key !== null && !current };
}

/** Save a Blob as a download. */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const filenameFrom = (headers, fallback) => {
  const m = /filename="?([^";]+)"?/i.exec(headers?.['content-disposition'] || '');
  return m ? m[1] : fallback;
};

// The deck's conditional fills: soft green / red vs the white base.
export const POS_FILL = '#C9E7CD';
export const NEG_FILL = '#F0CFCD';
